import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
    CheckCircle2, XCircle, Clock, Ban, MinusCircle, ChevronDown,
    Download, RotateCcw, Search, FlaskConical, ShieldCheck, ShieldAlert,
} from 'lucide-react';

const STATUS = {
    pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-600', Icon: Clock },
    pass: { label: 'Pass', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
    fail: { label: 'Fail', cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
    blocked: { label: 'Blocked', cls: 'bg-amber-100 text-amber-700', Icon: Ban },
    na: { label: 'N/A', cls: 'bg-slate-100 text-slate-400', Icon: MinusCircle },
};
const STATUS_KEYS = ['pending', 'pass', 'fail', 'blocked', 'na'];
const PRIORITY = {
    P0: 'bg-rose-100 text-rose-700',
    P1: 'bg-amber-100 text-amber-700',
    P2: 'bg-sky-100 text-sky-700',
};

export default function AdminUAT() {
    const [items, setItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);
    // filters
    const [fModule, setFModule] = useState('');
    const [fType, setFType] = useState('');
    const [fStatus, setFStatus] = useState('');
    const [fPriority, setFPriority] = useState('');
    const [q, setQ] = useState('');

    const loadSummary = useCallback(async () => {
        try { const r = await api.get('/admin/uat/summary'); setSummary(r.data); } catch { /* ignore */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (fModule) params.module = fModule;
            if (fType) params.type = fType;
            if (fStatus) params.status = fStatus;
            if (fPriority) params.priority = fPriority;
            if (q) params.q = q;
            const r = await api.get('/admin/uat/test-cases', { params });
            setItems(r.data.items || []);
            setModules(r.data.modules || []);
        } catch { toast.error('Failed to load test cases'); }
        finally { setLoading(false); }
    }, [fModule, fType, fStatus, fPriority, q]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    const patchCase = async (tc_id, fields) => {
        // optimistic
        setItems((prev) => prev.map((it) => (it.tc_id === tc_id ? { ...it, ...fields } : it)));
        try {
            await api.patch(`/admin/uat/test-cases/${tc_id}`, fields);
            if (fields.status) loadSummary();
        } catch { toast.error('Could not save'); load(); }
    };

    const resetCycle = async () => {
        if (!window.confirm('Reset ALL test cases to Pending and clear tester/notes? This starts a fresh UAT cycle.')) return;
        try { await api.post('/admin/uat/reset'); toast.success('UAT cycle reset'); load(); loadSummary(); }
        catch { toast.error('Reset failed'); }
    };

    const exportCsv = () => {
        const headers = ['TC ID', 'Module', 'Title', 'Type', 'Priority', 'Preconditions', 'Steps', 'Test Data', 'Expected Result', 'Status', 'Tester', 'Notes'];
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = items.map((it) => [
            it.tc_id, it.module, it.title, it.type, it.priority, it.preconditions,
            (it.steps || []).map((s, i) => `${i + 1}. ${s}`).join(' '),
            it.test_data, it.expected, STATUS[it.status]?.label || it.status, it.tester, it.notes,
        ].map(esc).join(','));
        const csv = [headers.map(esc).join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `propulse-uat-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const s = summary?.by_status || {};
    const cards = [
        { key: 'total', label: 'Total', value: summary?.total ?? '—', cls: 'text-slate-900', Icon: FlaskConical },
        { key: 'pass', label: 'Passed', value: s.pass ?? 0, cls: 'text-emerald-600', Icon: CheckCircle2 },
        { key: 'fail', label: 'Failed', value: s.fail ?? 0, cls: 'text-rose-600', Icon: XCircle },
        { key: 'blocked', label: 'Blocked', value: s.blocked ?? 0, cls: 'text-amber-600', Icon: Ban },
        { key: 'pending', label: 'Pending', value: s.pending ?? 0, cls: 'text-slate-500', Icon: Clock },
    ];

    const grouped = useMemo(() => {
        const map = {};
        items.forEach((it) => { (map[it.module] = map[it.module] || []).push(it); });
        return Object.entries(map);
    }, [items]);

    return (
        <div className="space-y-6" data-testid="admin-uat-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
                        <FlaskConical className="w-6 h-6 text-amber-500" /> UAT Test Cases
                    </h1>
                    <p className="text-sm text-slate-500">Exhaustive manual test catalog — record Pass / Fail / Blocked for each scenario. Results are saved automatically.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={exportCsv} data-testid="uat-export-csv"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                    <button onClick={resetCycle} data-testid="uat-reset"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-700">
                        <RotateCcw className="w-4 h-4" /> Reset cycle
                    </button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="uat-summary">
                {cards.map((c) => (
                    <div key={c.key} className="bg-white rounded-2xl border border-slate-100 p-4">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                            <c.Icon className="w-3.5 h-3.5" /> {c.label}
                        </div>
                        <div className={`text-2xl font-black mt-1 ${c.cls}`} data-testid={`uat-stat-${c.key}`}>{c.value}</div>
                    </div>
                ))}
                <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl p-4 text-slate-900">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide opacity-80">
                        <ShieldCheck className="w-3.5 h-3.5" /> Pass rate
                    </div>
                    <div className="text-2xl font-black mt-1" data-testid="uat-pass-rate">{summary?.pass_rate ?? 0}%</div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-100 p-3 flex flex-wrap items-center gap-2" data-testid="uat-filters">
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search id / title / expected…"
                        data-testid="uat-search"
                        className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-amber-400" />
                </div>
                <select value={fModule} onChange={(e) => setFModule(e.target.value)} data-testid="uat-filter-module"
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">All modules</option>
                    {modules.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="uat-filter-type"
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">All types</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                </select>
                <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} data-testid="uat-filter-priority"
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">All priorities</option>
                    <option value="P0">P0</option><option value="P1">P1</option><option value="P2">P2</option>
                </select>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="uat-filter-status"
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">All statuses</option>
                    {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}
                </select>
            </div>

            {/* List grouped by module */}
            {loading ? (
                <div className="py-12 text-center text-slate-400 text-sm">Loading test cases…</div>
            ) : items.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm" data-testid="uat-empty">No test cases match these filters.</div>
            ) : (
                <div className="space-y-5">
                    {grouped.map(([module, cases]) => (
                        <div key={module} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                <span className="font-bold text-sm text-slate-700">{module}</span>
                                <span className="text-xs text-slate-400">{cases.length} case{cases.length === 1 ? '' : 's'}</span>
                            </div>
                            {cases.map((it) => {
                                const st = STATUS[it.status] || STATUS.pending;
                                const isOpen = open === it.tc_id;
                                const TypeIcon = it.type === 'negative' ? ShieldAlert : ShieldCheck;
                                return (
                                    <div key={it.tc_id} className="border-b border-slate-50 last:border-0" data-testid={`uat-row-${it.tc_id}`}>
                                        <div className="flex items-center gap-3 px-4 py-3 text-sm">
                                            <button onClick={() => setOpen(isOpen ? null : it.tc_id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                                <span className="font-mono text-xs font-bold text-slate-500 w-24 shrink-0">{it.tc_id}</span>
                                                <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIORITY[it.priority] || 'bg-slate-100 text-slate-500'}`}>{it.priority}</span>
                                                <TypeIcon className={`w-4 h-4 shrink-0 ${it.type === 'negative' ? 'text-rose-400' : 'text-emerald-400'}`} />
                                                <span className="text-slate-800 truncate">{it.title}</span>
                                                <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                            </button>
                                            <select
                                                value={it.status}
                                                onChange={(e) => patchCase(it.tc_id, { status: e.target.value })}
                                                data-testid={`uat-status-${it.tc_id}`}
                                                className={`shrink-0 text-xs font-bold rounded-lg px-2 py-1 border-0 cursor-pointer ${st.cls}`}
                                            >
                                                {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}
                                            </select>
                                        </div>
                                        {isOpen && (
                                            <div className="px-4 pb-4 grid md:grid-cols-2 gap-4 bg-slate-50/60 text-sm">
                                                <div className="space-y-2">
                                                    <Field label="Preconditions" value={it.preconditions} />
                                                    <div>
                                                        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Steps</div>
                                                        <ol className="list-decimal ml-4 space-y-0.5 text-slate-700">
                                                            {(it.steps || []).map((step, i) => <li key={i}>{step}</li>)}
                                                        </ol>
                                                    </div>
                                                    <Field label="Test data" value={it.test_data} mono />
                                                </div>
                                                <div className="space-y-2">
                                                    <Field label="Expected result" value={it.expected} />
                                                    <div>
                                                        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Tester</div>
                                                        <input
                                                            defaultValue={it.tester}
                                                            onBlur={(e) => { if (e.target.value !== it.tester) patchCase(it.tc_id, { tester: e.target.value }); }}
                                                            placeholder="Your name"
                                                            data-testid={`uat-tester-${it.tc_id}`}
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
                                                    </div>
                                                    <div>
                                                        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">Notes / defects</div>
                                                        <textarea
                                                            defaultValue={it.notes}
                                                            onBlur={(e) => { if (e.target.value !== it.notes) patchCase(it.tc_id, { notes: e.target.value }); }}
                                                            rows={2}
                                                            placeholder="Observations, defect id, screenshots link…"
                                                            data-testid={`uat-notes-${it.tc_id}`}
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400 resize-y" />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const Field = ({ label, value, mono }) => (
    <div>
        <div className="text-[11px] font-bold uppercase text-slate-400 mb-1">{label}</div>
        <div className={`text-slate-700 ${mono ? 'font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1' : ''}`}>{value || '—'}</div>
    </div>
);
